import SensorResource from '@/api/resources/sensor'
import { SensorType } from '@/interfaces/enums'
import type { ISensor } from '@/interfaces/entities/sensor'
import { formatPeriodDate } from '@/utils/date/date-time'
import { ref, watch, type Ref } from 'vue'

export interface IDashboardTimeSeriesSensorData {
  sensor: ISensor
  start_date: string
  end_date: string
  readings: Record<string, Array<{ t?: string | number; v?: number }>>
}

const readingsCache = new Map<string, IDashboardTimeSeriesSensorData>()

export function clearDashboardTimeSeriesReadingsCache(): void {
  readingsCache.clear()
}

function cacheKey(sensorId: number, dateRange: string): string {
  return `${sensorId}:${dateRange}`
}

function resolutionForPeriod(period: [Date, Date]): string {
  const hours = (period[1].getTime() - period[0].getTime()) / (60 * 60 * 1000)
  return hours > 48 ? '15m' : '5m'
}

function isSupportedTimeSeriesType(sensorType: SensorType | string): boolean {
  return (
    sensorType === SensorType.ELECTRICITY ||
    sensorType === SensorType.TEMPERATURE ||
    sensorType === SensorType.DOOR_OPEN
  )
}

async function fetchReadingsForSensor(
  sensor: ISensor,
  period: [Date, Date],
): Promise<IDashboardTimeSeriesSensorData | null> {
  const dateRange = formatPeriodDate(period)
  const cached = readingsCache.get(cacheKey(sensor.id, dateRange))
  if (cached) return cached

  const resolution = resolutionForPeriod(period)
  const sensorType = sensor.sensor_type

  let response: {
    sensor?: ISensor
    start_date?: string
    end_date?: string
    readings?: Record<string, Array<{ t?: string | number; v?: number }>>
  } | null = null

  if (sensorType === SensorType.ELECTRICITY) {
    response = await SensorResource.getEnergySensorReadings({
      sensorId: sensor.id,
      dateRange,
      resolution,
      aggregation: 'mean',
    })
  } else if (sensorType === SensorType.TEMPERATURE) {
    response = await SensorResource.getTemperatureSensorReadings({
      sensorId: sensor.id,
      dateRange,
      resolution,
    })
  } else if (sensorType === SensorType.DOOR_OPEN) {
    response = await SensorResource.getOpenSensorReadings({
      sensorId: sensor.id,
      dateRange,
      resolution,
    })
  } else {
    return null
  }

  if (!response?.sensor || !response.readings) {
    return null
  }

  const sensorData: IDashboardTimeSeriesSensorData = {
    sensor: response.sensor,
    start_date: response.start_date ?? '',
    end_date: response.end_date ?? '',
    readings: response.readings,
  }

  readingsCache.set(cacheKey(sensor.id, dateRange), sensorData)
  return sensorData
}

export function useDashboardTimeSeriesReadings(
  sensor: Ref<ISensor | undefined>,
  period: Ref<[Date, Date] | null>,
  secondarySensor?: Ref<ISensor | undefined>,
) {
  const sensorData = ref<IDashboardTimeSeriesSensorData | null>(null)
  const secondarySensorData = ref<IDashboardTimeSeriesSensorData | null>(null)
  const isLoading = ref(false)
  const error = ref(false)
  const unsupportedSensorType = ref(false)

  async function refetch() {
    const currentSensor = sensor.value
    const currentSecondary = secondarySensor?.value
    const currentPeriod = period.value

    if (!currentSensor || !currentPeriod) {
      sensorData.value = null
      secondarySensorData.value = null
      isLoading.value = false
      error.value = false
      unsupportedSensorType.value = false
      return
    }

    if (!isSupportedTimeSeriesType(currentSensor.sensor_type)) {
      sensorData.value = null
      secondarySensorData.value = null
      isLoading.value = false
      error.value = false
      unsupportedSensorType.value = true
      return
    }

    if (currentSecondary && !isSupportedTimeSeriesType(currentSecondary.sensor_type)) {
      sensorData.value = null
      secondarySensorData.value = null
      isLoading.value = false
      error.value = false
      unsupportedSensorType.value = true
      return
    }

    unsupportedSensorType.value = false
    isLoading.value = true
    error.value = false

    try {
      if (currentSecondary) {
        const [primary, secondary] = await Promise.all([
          fetchReadingsForSensor(currentSensor, currentPeriod),
          fetchReadingsForSensor(currentSecondary, currentPeriod),
        ])
        sensorData.value = primary
        secondarySensorData.value = secondary
        if (!primary || !secondary) {
          error.value = true
        }
      } else {
        sensorData.value = await fetchReadingsForSensor(currentSensor, currentPeriod)
        secondarySensorData.value = null
        if (!sensorData.value) {
          error.value = true
        }
      }
    } catch {
      sensorData.value = null
      secondarySensorData.value = null
      error.value = true
    } finally {
      isLoading.value = false
    }
  }

  watch(
    [sensor, period, ...(secondarySensor ? [secondarySensor] : [])],
    () => refetch(),
    { immediate: true, deep: true },
  )

  return {
    sensorData,
    secondarySensorData,
    isLoading,
    error,
    unsupportedSensorType,
    refetch,
  }
}
